"use client";
/* oxlint-disable react/iframe-missing-sandbox, react/set-state-in-effect -- reviewed: legacy patterns, behavior preserved */

/** One compact preview and mixed thumbnail rail in editorial order. */
import { Film, ImageIcon, Pause, Play } from "lucide-react";
import { assetMirrorUrl, DEFAULT_PUBLIC_ORIGINS } from "@aphrody/shenron-config/public-urls";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReglages } from "@/lib/use-capacites";
import { assetUrl } from "@/lib/assets";
import { API_ORIGIN, ASSET_ORIGIN } from "@/lib/config";
import { youtubeEmbedUrl, youtubeThumbUrl } from "@/lib/youtube";
import { useGameGalleryUIText } from "@/lib/game-gallery-ui-text";

export type GalleryMedia = {
  type: "image" | "youtube";
  url: string;
  caption?: string | null;
};

function resolvePreview(item: GalleryMedia): {
  kind: "image" | "youtube";
  src: string;
  thumb: string | null;
} | null {
  if (item.type === "youtube") {
    const embed = youtubeEmbedUrl(item.url);
    if (!embed) return null;
    return { kind: "youtube", src: embed, thumb: youtubeThumbUrl(item.url) };
  }
  const src = item.url.startsWith("http") ? item.url : assetUrl(item.url);
  return { kind: "image", src, thumb: src };
}

/**
 * Largeur de l'image que `i.ytimg.com` renvoie, avec un statut 404, pour une
 * vidéo retirée ou privée : un gris uniforme de 120 × 90. Une vraie vignette
 * `hqdefault` fait toujours 480 × 360. Le corps de la réponse étant une image
 * valide, le navigateur peut déclencher `load` plutôt que `error` : on écarte
 * donc sur l'un OU l'autre signal, la taille couvrant le premier cas.
 */
const LARGEUR_VIGNETTE_ABSENTE = 120;

/**
 * Clés des vidéos dont la vignette YouTube signale l'indisponibilité.
 *
 * Sondage côté client, une requête par vidéo (la même URL que la vignette
 * affichée : le cache HTTP la sert une seule fois). Chaque clé n'est ajoutée
 * qu'une fois ; aucun nouvel essai n'est lancé pour une vidéo écartée.
 */
function useVideosIndisponibles(items: ReadonlyArray<ItemResolu>): ReadonlySet<string> {
  const [absentes, setAbsentes] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    let actif = true;
    const sondes: { img: HTMLImageElement; charge: () => void; ecarter: () => void }[] = [];
    for (const item of items) {
      if (item.kind !== "youtube" || !item.thumb) continue;
      const img = new Image();
      const ecarter = () => {
        if (!actif) return;
        setAbsentes((prev) => (prev.has(item.key) ? prev : new Set(prev).add(item.key)));
      };
      const charge = () => {
        if (img.naturalWidth <= LARGEUR_VIGNETTE_ABSENTE) ecarter();
      };
      img.addEventListener("load", charge);
      img.addEventListener("error", ecarter);
      img.src = item.thumb;
      sondes.push({ img, charge, ecarter });
    }
    return () => {
      actif = false;
      for (const { img, charge, ecarter } of sondes) {
        img.removeEventListener("load", charge);
        img.removeEventListener("error", ecarter);
      }
    };
  }, [items]);

  return absentes;
}

export function GameMediaGallery({ media, title }: { media: GalleryMedia[]; title: string }) {
  const items = useMemo(
    () =>
      media
        .map((m, i) => {
          const resolved = resolvePreview(m);
          if (!resolved) return null;
          return { ...m, ...resolved, key: `${m.type}-${i}-${m.url}` };
        })
        .filter((x): x is NonNullable<typeof x> => x != null),
    [media],
  );

  const indisponibles = useVideosIndisponibles(items);
  const available = useMemo(
    () => items.filter((i) => !indisponibles.has(i.key)),
    [items, indisponibles],
  );
  if (available.length === 0) return null;

  return (
    <div className="mb-8 min-w-0">
      <Gallery
        items={available}
        title={title}
        autoplay={available.length > 1 && available.every((item) => item.kind === "image")}
      />
    </div>
  );
}

type ItemResolu = GalleryMedia & {
  kind: "image" | "youtube";
  src: string;
  thumb: string | null;
  key: string;
};

const DELAI_AUTO_MS = 5000;

function Gallery({
  items,
  title,
  autoplay,
}: {
  items: ItemResolu[];
  title: string;
  autoplay: boolean;
}) {
  const t = useGameGalleryUIText();
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [cdnFallbacks, setCdnFallbacks] = useState<ReadonlySet<string>>(() => new Set());
  const [failedMedia, setFailedMedia] = useState<ReadonlySet<string>>(() => new Set());
  const active = Math.max(
    0,
    items.findIndex((item) => item.key === activeKey),
  );
  // `null` tant qu'on n'a pas interrogé les préférences système : on ne lance
  // rien avant de savoir si l'animation est la bienvenue.
  const [enMarche, setEnMarche] = useState<boolean | null>(null);
  const suspendu = useRef(false);
  const current = items[Math.min(active, Math.max(0, items.length - 1))];
  const cdnOrigin = ASSET_ORIGIN === API_ORIGIN ? DEFAULT_PUBLIC_ORIGINS.cdn : ASSET_ORIGIN;
  const mediaSource = (item: ItemResolu) => {
    if (item.kind !== "image" || !cdnFallbacks.has(item.key)) return item.src;
    return assetMirrorUrl(item.src, cdnOrigin, API_ORIGIN) ?? item.src;
  };
  const handleMediaFailure = (item: ItemResolu) => {
    if (
      item.kind === "image" &&
      !cdnFallbacks.has(item.key) &&
      assetMirrorUrl(item.src, cdnOrigin, API_ORIGIN)
    ) {
      setCdnFallbacks((previous) => new Set(previous).add(item.key));
      return;
    }
    setFailedMedia((previous) => new Set(previous).add(item.key));
  };

  // `lectureAuto` porte à la fois le choix de l'utilisateur
  // (`prefers-reduced-motion`) et l'état de sa machine et de sa connexion :
  // faire défiler des images toutes les 5 s sur un mobile en 2G dépense des
  // données pour un carrousel que personne n'a demandé.
  const { lectureAuto } = useReglages();
  useEffect(() => {
    if (!autoplay) return;
    setEnMarche(lectureAuto);
  }, [autoplay, lectureAuto]);

  useEffect(() => {
    if (!autoplay || enMarche !== true) return;
    const id = setInterval(() => {
      // L'onglet en arrière-plan continuerait d'avancer pour personne, et
      // laisserait le visiteur revenir sur une image qu'il n'a pas choisie.
      if (suspendu.current || document.hidden) return;
      setActiveKey((key) => {
        const index = Math.max(
          0,
          items.findIndex((item) => item.key === key),
        );
        return items[(index + 1) % items.length]!.key;
      });
    }, DELAI_AUTO_MS);
    return () => clearInterval(id);
  }, [autoplay, enMarche, items]);

  const choisir = useCallback((key: string) => {
    setActiveKey(key);
    // Un clic est une intention : on rend la main, on ne reprend pas la main
    // trois secondes plus tard sur une autre image.
    setEnMarche(false);
  }, []);

  if (!current) return null;

  return (
    <section aria-label={`${t("heading")} — ${title}`} className="min-w-0">
      <div className="mb-4 flex items-center justify-between gap-4 border-b border-white/10 pb-2">
        <h2 className="font-display text-[20px] font-bold text-white">
          {t("heading")}
          <span className="ml-2 font-mono text-[13px] font-normal text-white/45">
            {items.length}
          </span>
        </h2>
        {autoplay && enMarche !== null && (
          <button
            type="button"
            onClick={() => setEnMarche((v) => !v)}
            aria-label={t(enMarche ? "stopSlideshow" : "resumeSlideshow")}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-3 py-1.5 text-[11px] font-semibold text-white/60 transition-colors hover:border-dbz-orange/50 hover:text-white"
          >
            {enMarche ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
            {t(enMarche ? "pause" : "play")}
          </button>
        )}
      </div>

      <div
        className="overflow-hidden rounded-xl border border-white/[0.08] bg-black/40"
        onPointerEnter={() => (suspendu.current = true)}
        onPointerLeave={() => (suspendu.current = false)}
        onFocusCapture={() => (suspendu.current = true)}
        onBlurCapture={() => (suspendu.current = false)}
      >
        {/* Preview principale */}
        <div className="relative aspect-video w-full bg-black">
          {current?.kind === "youtube" ? (
            <iframe
              key={current.key}
              src={current.src}
              title={current.caption || t("videoTitle", { title })}
              className="absolute inset-0 h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : failedMedia.has(current.key) ? (
            <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/55">
              <ImageIcon className="h-9 w-9" aria-hidden="true" />
              <span className="sr-only">{t("imageUnavailable", { title })}</span>
            </span>
          ) : (
            <img
              key={current.key}
              src={mediaSource(current)}
              alt={current.caption || t("imageTitle", { title })}
              // `animate-[fadeIn]` : sans fondu, le passage automatique d'une
              // image à l'autre est un à-coup sec au milieu de la lecture.
              className="absolute inset-0 h-full w-full motion-safe:animate-[fade-in_400ms_ease-out] object-contain"
              onError={() => handleMediaFailure(current)}
            />
          )}
        </div>

        {current?.caption && (
          <p className="border-t border-white/[0.06] px-4 py-2 text-center text-[12px] text-white/55">
            {current.caption}
          </p>
        )}

        {/* Vignettes */}
        {items.length > 1 && (
          <div className="flex gap-2 overflow-x-auto border-t border-white/[0.06] bg-black/50 p-3 scrollbar-thin">
            {items.map((item, i) => {
              const isActive = i === active;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => choisir(item.key)}
                  aria-label={`${t(item.kind === "youtube" ? "video" : "image", { index: i + 1 })}${item.caption ? ` — ${item.caption}` : ""}`}
                  aria-current={isActive ? "true" : undefined}
                  className={`relative h-14 w-24 shrink-0 overflow-hidden rounded-md border-2 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dbz-orange ${
                    isActive
                      ? "border-dbz-orange shadow-[0_0_10px_rgba(255,178,0,0.35)]"
                      : "border-white/10 hover:border-white/30"
                  }`}
                >
                  {item.thumb && !failedMedia.has(item.key) ? (
                    <img
                      src={item.kind === "image" ? mediaSource(item) : item.thumb}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                      onError={() => handleMediaFailure(item)}
                    />
                  ) : (
                    <span className="flex h-full w-full items-center justify-center bg-zinc-900 text-white/50">
                      {item.kind === "youtube" ? (
                        <Film className="h-5 w-5" />
                      ) : (
                        <ImageIcon className="h-5 w-5" />
                      )}
                    </span>
                  )}
                  <span
                    aria-hidden="true"
                    className={`absolute ${item.kind === "youtube" ? "inset-0 flex items-center justify-center bg-black/35" : "bottom-1 right-1"}`}
                  >
                    <span className="rounded-full bg-black/70 p-1.5 text-white">
                      {item.kind === "youtube" ? (
                        <Play className="h-3.5 w-3.5" />
                      ) : (
                        <ImageIcon className="h-3 w-3" />
                      )}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
